import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useRevit } from '../../contexts/RevitContext';
import { useAuth } from '@clerk/clerk-react';
import { Instagram } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faYoutube } from '@fortawesome/free-brands-svg-icons';
import { handleLoginWithYouTube, logoutFromYt } from '../../utils/apiCalls';
import './Integrations.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const StripeMark = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <path
            fill="currentColor"
            d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"
        />
    </svg>
);

const Integrations = () => {
    const {
        username,
        youtubeName,
        setYoutubeName,
        youtubeThumbUrl,
        setYoutubeThumbUrl,
        youtubeLoading,
    } = useRevit();
    const { getToken } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [instagram, setInstagram] = useState({
        connected: false,
        username: null,
        name: null,
        profilePictureUrl: null,
    });
    const [instagramLoading, setInstagramLoading] = useState(false);
    const [youtubeActionLoading, setYoutubeActionLoading] = useState(false);
    const [isChecking, setIsChecking] = useState(true);
    const [stripe, setStripe] = useState({
        connected: false,
        name: null,
        email: null,
    });
    const [stripeLoading, setStripeLoading] = useState(false);
    const [stripeChecking, setStripeChecking] = useState(true);
    const [banner, setBanner] = useState(null);

    const checkInstagramConnection = useCallback(async () => {
        if (!username) return;

        try {
            const token = await getToken();
            const response = await axios.get(
                `https://${SERVER_URL}/api/integrations/instagram/status/${username}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setInstagram({
                connected: !!response.data.connected,
                username: response.data.username || null,
                name: response.data.name || null,
                profilePictureUrl: response.data.profilePictureUrl || null,
            });
        } catch (error) {
            console.error('Error checking Instagram connection:', error);
        } finally {
            setIsChecking(false);
        }
    }, [username, getToken]);

    const checkStripeConnection = useCallback(async () => {
        if (!username) {
            setStripeChecking(false);
            return;
        }

        try {
            const token = await getToken();
            const response = await axios.get(
                `https://${SERVER_URL}/api/integrations/stripe/status/${username}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setStripe({
                connected: !!response.data.connected,
                name: response.data.name || null,
                email: response.data.email || null,
            });
        } catch (error) {
            console.error('Error checking Stripe connection:', error);
        } finally {
            setStripeChecking(false);
        }
    }, [username, getToken]);

    useEffect(() => {
        checkInstagramConnection();
    }, [checkInstagramConnection]);

    useEffect(() => {
        checkStripeConnection();
    }, [checkStripeConnection]);

    useEffect(() => {
        const result = searchParams.get('instagram');
        const stripeResult = searchParams.get('stripe');
        const reason = searchParams.get('reason');
        const stripeReason = searchParams.get('stripe_reason');
        const error = searchParams.get('error');

        if (result === 'connected') {
            setBanner({ type: 'success', text: 'Instagram connected successfully.' });
            checkInstagramConnection();
        } else if (result === 'error' || (error && !stripeResult)) {
            setBanner({
                type: 'error',
                text: reason || 'Could not connect Instagram. Please try again.',
            });
        }

        if (stripeResult === 'connected') {
            setBanner({ type: 'success', text: 'Stripe connected successfully.' });
            checkStripeConnection();
        } else if (stripeResult === 'error') {
            setBanner({
                type: 'error',
                text: stripeReason || 'Could not connect Stripe. Please try again.',
            });
        }

        if (result || stripeResult || error || reason || stripeReason) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete('instagram');
            nextParams.delete('reason');
            nextParams.delete('error');
            nextParams.delete('stripe');
            nextParams.delete('stripe_reason');
            setSearchParams(nextParams, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const connectInstagram = async () => {
        if (!username) return;
        setInstagramLoading(true);
        try {
            const token = await getToken();
            const response = await axios.get(
                `https://${SERVER_URL}/api/integrations/instagram/auth?userId=${encodeURIComponent(username)}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.data?.url) {
                throw new Error('Instagram login URL was not returned');
            }
            window.location.href = response.data.url;
        } catch (error) {
            console.error('Error starting Instagram login:', error);
            setBanner({
                type: 'error',
                text: error.response?.data?.error || 'Could not start Instagram login. Please try again.',
            });
            setInstagramLoading(false);
        }
    };

    const disconnectInstagram = async () => {
        if (!window.confirm('Disconnect your Instagram account?')) return;

        setInstagramLoading(true);
        try {
            const token = await getToken();
            await axios.post(
                `https://${SERVER_URL}/api/integrations/instagram/disconnect`,
                { username },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setInstagram({
                connected: false,
                username: null,
                name: null,
                profilePictureUrl: null,
            });
            setBanner({ type: 'success', text: 'Instagram disconnected.' });
        } catch (error) {
            console.error('Error disconnecting Instagram:', error);
            setBanner({ type: 'error', text: 'Failed to disconnect Instagram. Please try again.' });
        } finally {
            setInstagramLoading(false);
        }
    };

    const stripeLabel = stripe.name || stripe.email;

    const connectStripe = async () => {
        if (!username) return;
        setStripeLoading(true);
        try {
            const token = await getToken();
            const response = await axios.get(
                `https://${SERVER_URL}/api/integrations/stripe/auth?userId=${encodeURIComponent(username)}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.data?.url) {
                throw new Error('Stripe login URL was not returned');
            }
            window.location.href = response.data.url;
        } catch (error) {
            console.error('Error starting Stripe connection:', error);
            setBanner({
                type: 'error',
                text: error.response?.data?.error || 'Could not start Stripe connection. Please try again.',
            });
            setStripeLoading(false);
        }
    };

    const disconnectStripe = async () => {
        if (!window.confirm('Disconnect your Stripe account?')) return;

        setStripeLoading(true);
        try {
            const token = await getToken();
            await axios.post(
                `https://${SERVER_URL}/api/integrations/stripe/disconnect`,
                { username },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setStripe({ connected: false, name: null, email: null });
            setBanner({ type: 'success', text: 'Stripe disconnected.' });
        } catch (error) {
            console.error('Error disconnecting Stripe:', error);
            setBanner({ type: 'error', text: 'Failed to disconnect Stripe. Please try again.' });
        } finally {
            setStripeLoading(false);
        }
    };

    const connectYoutube = () => {
        if (!username) return;
        handleLoginWithYouTube(username);
    };

    const disconnectYoutube = async () => {
        if (!window.confirm('Disconnect your YouTube channel?')) return;

        setYoutubeActionLoading(true);
        try {
            const token = await getToken();
            const success = await logoutFromYt(username, token);
            if (success) {
                setYoutubeName(null);
                setYoutubeThumbUrl(null);
                setBanner({ type: 'success', text: 'YouTube disconnected.' });
            } else {
                setBanner({ type: 'error', text: 'Failed to disconnect YouTube. Please try again.' });
            }
        } catch (error) {
            console.error('Error disconnecting YouTube:', error);
            setBanner({ type: 'error', text: 'Failed to disconnect YouTube. Please try again.' });
        } finally {
            setYoutubeActionLoading(false);
        }
    };

    return (
        <div className="integrations-container">
            <div className="integrations-header">
                <h1 className="heading-h2">Integrations</h1>
                <p className="integrations-subtitle">
                    Connect your accounts so Revtrack can pull in data from the platforms you already use.
                </p>
            </div>

            {banner && (
                <div className={`integrations-banner integrations-banner--${banner.type}`}>
                    {banner.text}
                </div>
            )}

            <div className="integration-cards">
                <div className="integration-card">
                    <div className="integration-header">
                        <span className="integration-logo integration-logo--youtube" aria-hidden="true">
                            <FontAwesomeIcon icon={faYoutube} />
                        </span>
                        <h2>YouTube Channel</h2>
                    </div>

                    <p className="integration-description">
                        Connect your YouTube channel to pull in views, video details, and channel data.
                    </p>

                    <div className="integration-status">
                        {youtubeLoading ? (
                            <span className="status-checking">Checking connection…</span>
                        ) : youtubeName ? (
                            <div className="status-connected-row">
                                {youtubeThumbUrl && (
                                    <img
                                        src={youtubeThumbUrl}
                                        alt=""
                                        className="integration-avatar"
                                    />
                                )}
                                <span className="status-connected">
                                    Connected as {youtubeName}
                                </span>
                            </div>
                        ) : (
                            <span className="status-disconnected">Not connected</span>
                        )}
                    </div>

                    {youtubeName ? (
                        <button
                            className="integration-button integration-button--secondary"
                            onClick={disconnectYoutube}
                            disabled={youtubeActionLoading}
                        >
                            {youtubeActionLoading ? 'Disconnecting…' : 'Disconnect YouTube'}
                        </button>
                    ) : (
                        <button
                            className="integration-button integration-button--youtube"
                            onClick={connectYoutube}
                            disabled={youtubeLoading || youtubeActionLoading || !username}
                        >
                            <FontAwesomeIcon icon={faYoutube} />
                            Connect YouTube
                        </button>
                    )}
                </div>

                <div className="integration-card">
                    <div className="integration-header">
                        <span className="integration-logo integration-logo--stripe" aria-hidden="true">
                            <StripeMark />
                        </span>
                        <h2>Stripe</h2>
                    </div>

                    <p className="integration-description">
                        Connect the Stripe account you use to charge your customers so Revtrack can track their payments.
                    </p>

                    <div className="integration-status">
                        {stripeChecking ? (
                            <span className="status-checking">Checking connection…</span>
                        ) : stripe.connected ? (
                            <span className="status-connected">
                                Connected{stripeLabel ? ` as ${stripeLabel}` : ''}
                            </span>
                        ) : (
                            <span className="status-disconnected">Not connected</span>
                        )}
                    </div>

                    {stripe.connected ? (
                        <button
                            className="integration-button integration-button--secondary"
                            onClick={disconnectStripe}
                            disabled={stripeLoading}
                        >
                            {stripeLoading ? 'Disconnecting…' : 'Disconnect Stripe'}
                        </button>
                    ) : (
                        <button
                            className="integration-button integration-button--stripe"
                            onClick={connectStripe}
                            disabled={stripeLoading || stripeChecking || !username}
                        >
                            <StripeMark />
                            {stripeLoading ? 'Redirecting…' : 'Connect Stripe'}
                        </button>
                    )}
                </div>

                <div className="integration-card">
                    <div className="integration-header">
                        <span className="integration-logo integration-logo--instagram" aria-hidden="true">
                            <Instagram size={18} color="#fff" />
                        </span>
                        <h2>Instagram</h2>
                    </div>

                    <p className="integration-description">
                        Connect your Instagram professional account with Instagram Login.
                        You will sign in with Instagram. Business and Creator accounts are supported.
                    </p>

                    <div className="integration-status">
                        {isChecking ? (
                            <span className="status-checking">Checking connection…</span>
                        ) : instagram.connected ? (
                            <div className="status-connected-row">
                                {instagram.profilePictureUrl && (
                                    <img
                                        src={instagram.profilePictureUrl}
                                        alt=""
                                        className="integration-avatar"
                                    />
                                )}
                                <span className="status-connected">
                                    Connected{instagram.username ? ` as @${instagram.username}` : ''}
                                </span>
                            </div>
                        ) : (
                            <span className="status-disconnected">Not connected</span>
                        )}
                    </div>

                    {instagram.connected ? (
                        <button
                            className="integration-button integration-button--secondary"
                            onClick={disconnectInstagram}
                            disabled={instagramLoading}
                        >
                            {instagramLoading ? 'Disconnecting…' : 'Disconnect Instagram'}
                        </button>
                    ) : (
                        <button
                            className="integration-button integration-button--instagram"
                            onClick={connectInstagram}
                            disabled={instagramLoading || isChecking || !username}
                        >
                            <Instagram size={16} />
                            {instagramLoading ? 'Redirecting…' : 'Connect Instagram'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Integrations;
